import { PRO_SUBSCRIPTION } from "@/constants";
import { useRedirectToCheckout } from "@/features/Checkout/hooks/useRedirectToCheckout";
import { useCreditConsumption } from "@/features/Credits/hooks/useCreditConsumption";
import { SubscriptionPanel } from "@/features/Subscriptions/components/SubscriptionPanel/SubscriptionPanel";
import { useProducts } from "@/features/Subscriptions/hooks/useProducts";
import { useSubscription } from "@/features/Subscriptions/hooks/useSubscription";
import { logout_event, toggle_dark_mode_event, toggle_light_mode_event } from "@/services/posthog/events";
import { enable_purchase_credits_flag, enable_manage_subscription_flag } from "@/services/posthog/feature_flags";
import { useAnalytics } from "@/services/posthog/hooks";
import { logout } from "@/services/supabase/auth/actions/logout";
import { useAuth } from "@/services/supabase/auth/AuthContext/useAuth";
import {
  Avatar,
  Box,
  Button,
  MenuItem as ChakraMenuItem,
  Divider,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  Progress,
  Stack,
  Text,
  useColorMode,
  useDisclosure,
} from "@chakra-ui/react";
import { Icon, MenuItem } from "@pufflig/prompt-studio-ui";
import { format } from "date-fns";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { useState } from "react";

const AvatarMenuItem = () => {
  const { user } = useAuth();
  return (
    <ChakraMenuItem
      height="auto"
      padding="spacing-xs"
      cursor="default"
      _hover={{
        backgroundColor: "transparent",
      }}
    >
      <Flex gap="spacing-xs" alignItems="center">
        <Avatar name={user?.user_metadata.name} src={user?.user_metadata.avatar_url} size="sm" />
        <Stack gap="0">
          <Text textStyle="label/M/regular">{user?.user_metadata.name}</Text>
          <Text textStyle="label/S/regular" color="foreground.secondary">
            {user?.email}
          </Text>
        </Stack>
      </Flex>
    </ChakraMenuItem>
  );
};

const CREDIT_DISPLAY_AMOUNT = 1000000;

const getCreditPrice = (products: any) => {
  return products?.find((product: any) => product.name === "credit")?.prices?.[0].stripe_price_id;
};

export const UserMenu = (props: any) => {
  const { teamId } = props;
  const { user } = useAuth();
  const { colorMode, setColorMode } = useColorMode();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { data: subscription } = useSubscription();
  const { data: creditInfo, isLoading } = useCreditConsumption(teamId);
  const { data: products } = useProducts();
  const analytics = useAnalytics();
  const canPurchaseCredits = useFeatureFlagEnabled(enable_purchase_credits_flag);
  const canManageSubscription = useFeatureFlagEnabled(enable_manage_subscription_flag);

  const creditPrice = getCreditPrice(products);

  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);

  const redirectToCheckout = useRedirectToCheckout();

  const goToCheckout = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsCheckoutLoading(true);
    const lineItems = [
      {
        price: creditPrice,
        quantity: 25,
      },
    ];
    await redirectToCheckout(lineItems, false);
    setIsCheckoutLoading(false);
  };

  const showCreditPurchaseOption = canPurchaseCredits && !teamId;

  if (isLoading || !creditInfo) {
    return null;
  }

  const creditQuota = creditInfo.creditQuota / CREDIT_DISPLAY_AMOUNT;
  const subscriptionCreditsUsed = creditInfo.subscriptionCreditsUsed / CREDIT_DISPLAY_AMOUNT;
  const additionalCredits = creditInfo.additionalCredits / CREDIT_DISPLAY_AMOUNT;
  const resetDate = creditInfo.resetDate;

  const totalRemainingCreditsInPercentage = 100 - (subscriptionCreditsUsed / (creditQuota + additionalCredits)) * 100;
  const monthlyRemainingCredits = creditQuota - subscriptionCreditsUsed;

  return (
    <>
      <SubscriptionPanel isOpen={isOpen} onClose={onClose} />
      <Menu>
        <Flex as={MenuButton}>
          <Avatar
            as={MenuButton}
            alignItems={"center"}
            tabIndex={0}
            cursor="pointer"
            name={user?.user_metadata.name}
            src={user?.user_metadata.avatar_url}
            size="sm"
          />
        </Flex>
        <MenuList>
          <AvatarMenuItem />
          <Flex paddingX="1" mb="1" flexDirection="column" gap="spacing-xs">
            <Text textStyle="label/S/medium" color="foreground.secondary">
              Remaining Credits {teamId ? "(team)" : ""}
            </Text>
            <Progress value={totalRemainingCreditsInPercentage} size="xs" colorScheme="orange" />
            <Box>
              <Flex justifyContent="space-between">
                <Text textStyle="label/S/regular" color="foreground.primary">
                  Monthly credits
                </Text>
                <Text textStyle="label/S/regular" color="foreground.primary">
                  {Math.abs(monthlyRemainingCredits).toFixed(2)} / {creditQuota}
                </Text>
              </Flex>
              <Text textStyle="label/S/regular" color="text.secondary">
                Usage resets {format(resetDate, "do MMMM")}
              </Text>
            </Box>
            <Flex justifyContent="space-between">
              <Text textStyle="label/S/regular" color="foreground.primary">
                Additional credits
              </Text>
              <Text textStyle="label/S/regular" color="foreground.primary">
                {additionalCredits.toFixed(2)}
              </Text>
            </Flex>
            {showCreditPurchaseOption && (
              <Stack mt="spacing-md">
                <Text textAlign="center">or</Text>
                <Button
                  isLoading={isCheckoutLoading}
                  variant="ghost"
                  size="sm"
                  color="foreground.primary"
                  _hover={{}}
                  onClick={goToCheckout}
                >
                  Purchase additional credits
                </Button>
              </Stack>
            )}
          </Flex>
          <Divider marginY="spacing-xxs" />
          {canManageSubscription && (
            <>
              <MenuItem
                onClick={onOpen}
                leftIcon={<Icon name="card" />}
                primaryLabel="Manage Subscription"
                secondaryLabel={subscription?.type === PRO_SUBSCRIPTION ? "Pro plan" : "Free plan"}
              />
              <Divider marginY="spacing-xxs" />
            </>
          )}
          <MenuItem
            leftIcon={colorMode === "light" ? <Icon name="moon" /> : <Icon name="sun-1" />}
            primaryLabel={colorMode === "light" ? "Switch to dark mode" : "Switch to light mode"}
            onClick={() => {
              analytics.capture(colorMode === "light" ? toggle_dark_mode_event : toggle_light_mode_event, {
                from: "user_menu",
              });
              setColorMode(colorMode === "light" ? "dark" : "light");
            }}
          />
          <MenuItem
            leftIcon={<Icon name="logout-1" />}
            primaryLabel="Log out"
            onClick={() => {
              analytics.capture(logout_event, { from: "user_menu" });
              logout();
            }}
          />
        </MenuList>
      </Menu>
    </>
  );
};
